<template>
  <section class="not-found-page">
    <div class="not-found-page__glow not-found-page__glow--left"></div>
    <div class="not-found-page__glow not-found-page__glow--right"></div>

    <div class="not-found-page__content">
      <div class="not-found-page__badge">{{ t("notFound.badge") }}</div>
      <p class="not-found-page__code">{{ statusCodeLabel }}</p>
      <h1 class="not-found-page__title">{{ titleText }}</h1>
      <p class="not-found-page__description">{{ descriptionText }}</p>

      <div v-if="displayPath" class="not-found-page__path">
        <span>{{ t("notFound.pathLabel") }}</span>
        <code>{{ displayPath }}</code>
      </div>

      <div class="not-found-page__actions">
        <button type="button" class="not-found-page__button not-found-page__button--primary" @click="goHome">
          {{ t("notFound.goHome") }}
        </button>
        <a class="not-found-page__button not-found-page__button--secondary" :href="contactHref">
          {{ t("notFound.contactTech") }}
        </a>
      </div>

      <p class="not-found-page__support">
        {{ t("notFound.supportHint") }}
        <a :href="contactHref">{{ supportEmail }}</a>
      </p>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  statusCode: {
    type: [Number, String],
    default: 404,
  },
  statusMessage: {
    type: String,
    default: "",
  },
  path: {
    type: String,
    default: "",
  },
  clearErrorOnHome: {
    type: Boolean,
    default: false,
  },
});

const supportEmail = "py3763984@gmail.com";
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const normalizedStatusCode = computed(() => {
  const parsed = Number(props.statusCode);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 404;
});

const statusCodeLabel = computed(() => String(normalizedStatusCode.value));
const displayPath = computed(() => {
  const rawPath = String(props.path || route.fullPath || "").trim();
  return rawPath || "";
});

const titleText = computed(() => {
  if (normalizedStatusCode.value === 403) {
    return t("notFound.forbiddenTitle");
  }
  return t("notFound.title");
});

const descriptionText = computed(() => {
  if (normalizedStatusCode.value === 403) {
    return t("notFound.forbiddenDescription");
  }

  const message = String(props.statusMessage || "").trim();
  if (message && message !== "Not Found" && message !== "Page not found") {
    return message;
  }
  return t("notFound.description");
});

const contactHref = computed(() => {
  const subject = encodeURIComponent(t("notFound.emailSubject"));
  const body = encodeURIComponent(
    `${t("notFound.emailBodyIntro")}\n${displayPath.value || "/"}\n\n${t("notFound.emailBodyCode")}: ${statusCodeLabel.value}`
  );
  return `mailto:${supportEmail}?subject=${subject}&body=${body}`;
});

function goHome() {
  if (props.clearErrorOnHome) {
    clearError({ redirect: "/" });
    return;
  }
  router.push("/");
}
</script>

<style scoped lang="less">
.not-found-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at top left, rgba(96, 165, 250, 0.2), transparent 35%),
    radial-gradient(circle at bottom right, rgba(56, 189, 248, 0.18), transparent 32%),
    linear-gradient(135deg, #eef7ff 0%, #f8fcff 48%, #e8f4ff 100%);
}

.not-found-page__glow {
  position: absolute;
  width: 340px;
  height: 340px;
  border-radius: 999px;
  filter: blur(18px);
  opacity: 0.45;
  pointer-events: none;
}

.not-found-page__glow--left {
  top: -72px;
  left: -96px;
  background: rgba(96, 165, 250, 0.28);
}

.not-found-page__glow--right {
  right: -80px;
  bottom: -96px;
  background: rgba(34, 197, 246, 0.22);
}

.not-found-page__content {
  position: relative;
  width: min(100%, 760px);
  padding: 48px 40px;
  border-radius: 32px;
  border: 1px solid rgba(96, 165, 250, 0.14);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 24px 80px rgba(37, 99, 235, 0.12);
  backdrop-filter: blur(10px);
  text-align: center;
}

.not-found-page__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.08);
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.not-found-page__code {
  margin-top: 20px;
  color: #2f6fed;
  font-size: clamp(48px, 10vw, 108px);
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.not-found-page__title {
  margin-top: 16px;
  color: #19335a;
  font-size: clamp(28px, 4vw, 44px);
  line-height: 1.18;
  font-weight: 700;
}

.not-found-page__description {
  max-width: 560px;
  margin: 16px auto 0;
  color: #5d7291;
  font-size: 16px;
  line-height: 1.75;
}

.not-found-page__path {
  margin: 24px auto 0;
  max-width: 100%;
  padding: 14px 18px;
  border-radius: 18px;
  background: #eef6ff;
  color: #61758f;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-all;
}

.not-found-page__path code {
  display: block;
  margin-top: 6px;
  color: #2563eb;
  font-size: 13px;
}

.not-found-page__actions {
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
}

.not-found-page__button {
  min-width: 180px;
  min-height: 50px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease;
}

.not-found-page__button:hover {
  transform: translateY(-1px);
}

.not-found-page__button--primary {
  background: linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%);
  color: #fff;
  box-shadow: 0 14px 30px rgba(59, 130, 246, 0.24);
}

.not-found-page__button--secondary {
  background: rgba(255, 255, 255, 0.92);
  border-color: rgba(59, 130, 246, 0.22);
  color: #2563eb;
}

.not-found-page__support {
  margin-top: 18px;
  color: #61758f;
  font-size: 14px;
  line-height: 1.7;
}

.not-found-page__support a {
  color: #2563eb;
  font-weight: 600;
}

@media (max-width: 767px) {
  .not-found-page {
    padding: 16px;
  }

  .not-found-page__content {
    padding: 36px 20px;
    border-radius: 24px;
  }

  .not-found-page__description {
    font-size: 15px;
  }

  .not-found-page__actions {
    gap: 12px;
  }

  .not-found-page__button {
    width: 100%;
  }
}
</style>
